import React from 'react';
import Reveal from '../components/Reveal';
import { appSections, deviceSupport, faqs, iosDetails } from '../content/siteContent';

const Support: React.FC = () => {
  return (
    <div className="pb-20">
      <section className="px-6 pt-20 pb-12 text-center md:pt-32">
        <h1 className="mx-auto max-w-3xl text-4xl font-semibold tracking-tight text-white md:text-6xl">
          Support.
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-lg text-white/50">
          Get help with Vizo, presets, exports, and subscriptions.
        </p>
      </section>

      <section className="px-6 py-8">
        <div className="apple-section">
          <div className="grid gap-4 md:grid-cols-2">
            <Reveal>
              <div className="apple-card">
                <h2 className="text-2xl font-semibold text-white">Contact</h2>
              <p className="mt-3 text-sm text-white/50">
                Send a message with your device model, iOS version, and a short description of the issue.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-white/70">
                <li>Email: vizomusic@proton.me</li>
                <li>Response target: within 24-48 hours</li>
                <li>App: Vizo - Music Visualizer</li>
              </ul>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="apple-card">
                <h2 className="text-2xl font-semibold text-white">Useful Links</h2>
              <ul className="mt-4 space-y-2 text-sm">
                <li><a className="apple-link" href="https://apps.apple.com/tr/app/vizo-music-visualizer/id6749325448" target="_blank" rel="noopener noreferrer">Open the App Store listing</a></li>
                <li><a className="apple-link" href="/privacy">Read the Privacy Policy</a></li>
                <li><a className="apple-link" href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" target="_blank" rel="noopener noreferrer">Read the Terms of Use</a></li>
              </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="apple-section">
          <Reveal>
            <h2 className="mb-8 text-center text-3xl font-semibold tracking-tight text-white md:text-4xl">Common questions</h2>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-2">
            {faqs.map((item, i) => (
              <Reveal key={item.question} delay={(i % 2) * 100}>
                <div className="apple-card">
                <h3 className="text-lg font-semibold text-white">{item.question}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{item.answer}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="apple-section">
          <Reveal>
            <h2 className="mb-8 text-center text-3xl font-semibold tracking-tight text-white md:text-4xl">Main app sections</h2>
          </Reveal>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {appSections.map((section, i) => (
              <Reveal key={section.title} delay={(i % 3) * 100}>
                <div className="apple-card">
                <h3 className="text-xl font-semibold text-white">{section.title}</h3>
                <ul className="mt-4 space-y-2.5">
                  {section.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm text-white/70">
                      <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="apple-section">
          <Reveal>
            <div className="apple-card">
              <h2 className="text-2xl font-semibold text-white">Before you contact support</h2>
            <ul className="mt-5 space-y-2.5">
              <li className="flex items-start gap-3 text-sm text-white/70">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                <span>Confirm your app version and iOS version</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/70">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                <span>Describe the exact export, preset, or subscription issue</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/70">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                <span>Share the steps needed to reproduce the problem</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-white/70">
                <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent"></span>
                <span>Include screenshots if a visual glitch or message appears</span>
              </li>
            </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="apple-section">
          <Reveal>
            <h2 className="mb-8 text-center text-3xl font-semibold tracking-tight text-white md:text-4xl">Supported devices</h2>
          </Reveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {deviceSupport.map((device, i) => (
              <Reveal key={device.title} delay={i * 100}>
                <div className="apple-card flex flex-col items-center text-center">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-white/[0.06] text-2xl">
                    {device.icon === 'iPhone' && '📱'}
                    {device.icon === 'iPad' && '📲'}
                    {device.icon === 'Mac' && '💻'}
                    {device.icon === 'Vision' && '🥽'}
                  </div>
                  <h3 className="text-lg font-semibold text-white">{device.title}</h3>
                  <p className="mt-2 text-sm text-white/50">{device.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="apple-section">
          <Reveal>
            <div className="apple-card">
              <h2 className="text-2xl font-semibold text-white">Platform details</h2>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {iosDetails.map((detail) => (
                <div key={detail} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm text-white/70">
                  {detail}
                </div>
              ))}
            </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Support;
